import { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaUpload } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const CreativeWritingActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => { // eslint-disable-line no-unused-vars
  const [content, setContent] = useState({
    prompts: [{
      title: '',
      description: '',
      image: null,
      keywords: [],
      minWords: 50,
      maxWords: 200,
      rubric: {
        creativity: 25,
        grammar: 25,
        vocabulary: 25,
        relevance: 25
      }
    }]
  });
  const [uploadingImages, setUploadingImages] = useState({});

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = typeof initialContent === 'string' 
          ? JSON.parse(initialContent) 
          : initialContent;
        setContent(parsed);
      } catch (error) {

        // Ignore errors
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const updatePrompt = (index, field, value) => {
    const newPrompts = [...content.prompts];
    newPrompts[index] = { ...newPrompts[index], [field]: value };
    setContent({ ...content, prompts: newPrompts });
  };

  const updateRubric = (index, field, value) => {
    const newPrompts = [...content.prompts];
    newPrompts[index] = {
      ...newPrompts[index],
      rubric: { ...newPrompts[index].rubric, [field]: parseInt(value) || 0 }
    };
    setContent({ ...content, prompts: newPrompts });
  };

  const addPrompt = () => {
    setContent({
      ...content,
      prompts: [...content.prompts, {
        title: '',
        description: '',
        image: null,
        keywords: [],
        minWords: 50,
        maxWords: 200,
        rubric: {
          creativity: 25,
          grammar: 25,
          vocabulary: 25,
          relevance: 25
        }
      }]
    });
  };

  const removePrompt = (index) => {
    if (content.prompts.length > 1) {
      const newPrompts = content.prompts.filter((_, i) => i !== index);
      setContent({ ...content, prompts: newPrompts });
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    const uploadKey = `prompt-${index}`;
    setUploadingImages(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const fileName = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'creative_writing',
        fileName: file.name,
        itemId: `prompt${index}`
      });
      
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      updatePrompt(index, 'image', downloadURL);
      alert('Image uploaded successfully!');
    } catch (error) {

      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImages(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleKeywordsChange = (index, value) => {
    const keywords = value.split(',').map(k => k.trim()).filter(k => k);
    updatePrompt(index, 'keywords', keywords);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">📝 Creative Writing Activity</h3>
        <p className="text-blue-700 text-sm">
          Create writing prompts for students to practice creative writing. Each prompt can include an image, keywords, and word count requirements.
        </p>
      </div>

      {content.prompts.map((prompt, index) => (
        <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-bold text-gray-900">Prompt {index + 1}</h4>
            {content.prompts.length > 1 && (
              <button
                type="button"
                onClick={() => removePrompt(index)}
                className="text-red-600 hover:text-red-800 transition-colors p-2 hover:bg-red-50 rounded-lg"
              >
                <FaTrash />
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., My Dream Vacation"
                value={prompt.title || ''}
                onChange={(e) => updatePrompt(index, 'title', e.target.value)}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description/Instructions *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe what students should write about..."
                rows="4"
                value={prompt.description || ''}
                onChange={(e) => updatePrompt(index, 'description', e.target.value)}
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Image (Optional)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(index, e.target.files[0])}
                  className="hidden"
                  id={`image-upload-${index}`}
                  disabled={uploadingImages[`prompt-${index}`]}
                />
                <label
                  htmlFor={`image-upload-${index}`}
                  className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer ${
                    uploadingImages[`prompt-${index}`] ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <FaUpload />
                  <span>{uploadingImages[`prompt-${index}`] ? 'Uploading...' : 'Upload Image'}</span>
                </label>
                {prompt.image && (
                  <div className="flex items-center gap-2">
                    <img src={prompt.image} alt="Prompt" className="h-16 w-16 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => updatePrompt(index, 'image', null)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords (comma-separated)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., adventure, travel, family"
                value={prompt.keywords?.join(', ') || ''}
                onChange={(e) => handleKeywordsChange(index, e.target.value)}
              />
            </div>

            {/* Word Count */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Words
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={prompt.minWords || 50}
                  onChange={(e) => updatePrompt(index, 'minWords', parseInt(e.target.value) || 50)}
                  min="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Words
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={prompt.maxWords || 200}
                  onChange={(e) => updatePrompt(index, 'maxWords', parseInt(e.target.value) || 200)}
                  min="20"
                />
              </div>
            </div>

            {/* Rubric */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grading Rubric (Total should be 100)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Creativity</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={prompt.rubric?.creativity || 25}
                    onChange={(e) => updateRubric(index, 'creativity', e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Grammar</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={prompt.rubric?.grammar || 25}
                    onChange={(e) => updateRubric(index, 'grammar', e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Vocabulary</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={prompt.rubric?.vocabulary || 25}
                    onChange={(e) => updateRubric(index, 'vocabulary', e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Relevance</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={prompt.rubric?.relevance || 25}
                    onChange={(e) => updateRubric(index, 'relevance', e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Total: {Object.values(prompt.rubric || {}).reduce((sum, val) => sum + val, 0)}/100
              </p>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addPrompt}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
      >
        <FaPlus />
        <span>Add Another Prompt</span>
      </button>
    </div>
  );
};

export const validateCreativeWritingContent = (content) => {
  const errors = [];

  if (!content.prompts || content.prompts.length === 0) {
    errors.push('At least one writing prompt is required');
    return errors;
  }

  content.prompts.forEach((prompt, index) => {
    if (!prompt.title || prompt.title.trim() === '') {
      errors.push(`Prompt ${index + 1}: Title is required`);
    }
    if (!prompt.description || prompt.description.trim() === '') {
      errors.push(`Prompt ${index + 1}: Description is required`);
    }
    if (prompt.minWords && prompt.maxWords && prompt.minWords > prompt.maxWords) {
      errors.push(`Prompt ${index + 1}: Minimum words cannot be greater than maximum words`);
    }
    if (prompt.rubric) {
      const total = Object.values(prompt.rubric).reduce((sum, val) => sum + val, 0);
      if (total !== 100) {
        errors.push(`Prompt ${index + 1}: Rubric total must equal 100 (currently ${total})`);
      }
    }
  });

  return errors;
};

export default CreativeWritingActivity;
